import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { dashboardApi, reportsApi, partiesApi, getStoredUser, type DashboardSummary, type CounterpartyStat, type Party } from '../api/client.js';
import { formatAmount } from '../components/Layout.js';

export default function Dashboard() {
  const [data, setData] = useState<DashboardSummary | null>(null);
  const [error, setError] = useState('');
  // 客户切换器：选中后「往来账龄与超期」按该客户出数；收入/支出/待办保持公司整体口径
  const me = getStoredUser();
  const partyStoreKey = `zia_dash_party_${me?.id ?? 'anon'}`;
  // 同步读存档选中客户作初值：首屏 summary 请求即带 partyId，消除「先全量后切客户」的口径闪烁
  const [partyId, setPartyId] = useState(() => localStorage.getItem(partyStoreKey) || '');
  const [parties, setParties] = useState<Party[]>([]);
  const [notice, setNotice] = useState('');

  // 可见客户清单（服务端已按 8.4 角色圈定：会计=自己+公开；主管/管理员=本租户全部）
  useEffect(() => {
    partiesApi.list({ isActive: true, pageSize: 500 })
      .then((r) => {
        const list = r.parties.filter((p) => p.isActive);
        setParties(list);
        // 以可见清单为准确认选中态：存档不在清单内（被停用/转走/换了账号）则静默回落「全部」
        const saved = localStorage.getItem(partyStoreKey) || '';
        if (saved && !list.some((p) => p.id === saved)) {
          localStorage.removeItem(partyStoreKey);
          setPartyId('');
        }
      })
      .catch(() => { /* 选择器数据加载失败不阻断主仪表盘，仅没有可选项 */ });
  }, [partyStoreKey]);

  useEffect(() => {
    dashboardApi.summary(undefined, partyId ? { partyId } : undefined)
      .then((d) => { setData(d); setError(''); })
      .catch((e: any) => {
        if (e?.status === 403 || e?.status === 404) {
          // 归属变更/档案失效：提示并回落「全部」后重拉
          setNotice('之前选中的客户已不在你的可见范围（或档案已停用），已回落为「全部客户」口径');
          localStorage.removeItem(partyStoreKey);
          setPartyId('');
          return;
        }
        setError(e.message);
      });
  }, [partyId, partyStoreKey]);

  const pickParty = (id: string) => {
    setNotice('');
    setPartyId(id);
    if (id) localStorage.setItem(partyStoreKey, id);
    else localStorage.removeItem(partyStoreKey);
  };

  if (error) {
    return <div className="card p-4 border-red-200 bg-red-50/60 text-red-700 text-sm">加载失败：{error}</div>;
  }
  if (!data) {
    return (
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        {Array.from({ length: 5 }).map((_, i) => (
          <div key={i} className="card card-pad animate-pulse">
            <div className="h-3 w-20 bg-slate-200 rounded mb-4" />
            <div className="h-7 w-24 bg-slate-200 rounded" />
          </div>
        ))}
      </div>
    );
  }

  const net = data.month.netProfit;
  const cash = data.month.cashNetChange;
  const monthCards = [
    { label: `${data.month.period} 收入`, value: data.month.totalRevenue, icon: '📈', grad: 'from-emerald-500 to-teal-600', tone: 'text-emerald-600' },
    { label: '支出（费用）', value: data.month.totalExpense, icon: '📉', grad: 'from-orange-500 to-amber-600', tone: 'text-orange-600' },
    { label: '净利润', value: net, icon: '💎', grad: net >= 0 ? 'from-brand-500 to-violet-600' : 'from-rose-500 to-red-600', tone: net >= 0 ? 'text-brand-600' : 'text-red-600' },
    { label: '资金净变动（已入账）', value: cash, icon: '🏦', grad: 'from-sky-500 to-indigo-600', tone: cash >= 0 ? 'text-slate-900' : 'text-red-600' },
    { label: '本月微信实收', value: data.wechat.monthInflow, icon: '💬', grad: 'from-teal-500 to-emerald-600', tone: 'text-teal-600', sub: '流水口径 · 含未确认' },
  ];

  const todos = [
    { label: '待过账凭证', value: data.journal.draft, to: '/journal', icon: '🟠', chip: 'bg-orange-500' },
    { label: '待审核凭证', value: data.journal.posted, to: '/journal', icon: '🔵', chip: 'bg-brand-500' },
    { label: '待生成凭证流水', value: data.wechat.confirmedNoJournal, to: '/wechat', icon: '🟣', chip: 'bg-violet-500' },
    { label: '未处理客户信号', value: data.signals.unread, to: '/monitor', icon: '🟪', chip: 'bg-fuchsia-500' },
    { label: '启用科目', value: data.accounts.total, to: '/accounts', icon: '⚪️', chip: 'bg-slate-400' },
  ];

  const pending = [
    { label: '高置信度', hint: '可批量确认', value: data.wechat.pending.high, tone: 'text-emerald-600', bar: 'bg-emerald-500' },
    { label: '中置信度', hint: '下拉选择', value: data.wechat.pending.medium, tone: 'text-amber-600', bar: 'bg-amber-500' },
    { label: '低置信度', hint: '逐条编辑', value: data.wechat.pending.low, tone: 'text-red-600', bar: 'bg-red-500' },
  ];

  return (
    <div className="space-y-6">
      {notice && (
        <div className="p-3 bg-amber-50 border border-amber-200 text-amber-700 rounded-lg text-sm flex items-center justify-between">
          <span>{notice}</span>
          <button onClick={() => setNotice('')} className="text-amber-400 hover:text-amber-600">×</button>
        </div>
      )}
      {/* Hero */}
      <div className="relative overflow-hidden rounded-3xl bg-brand-gradient p-6 sm:p-8 text-white shadow-glow animate-fade-in-up">
        <div className="absolute -right-8 -top-10 w-48 h-48 rounded-full bg-white/10 blur-2xl" />
        <div className="absolute right-24 -bottom-16 w-56 h-56 rounded-full bg-violet-400/20 blur-3xl" />
        <div className="relative flex items-center justify-between flex-wrap gap-4">
          <div>
            <p className="text-white/70 text-sm">经营看板 · {data.month.period}</p>
            <h2 className="text-2xl sm:text-3xl font-bold tracking-tight mt-1">本月经营全景</h2>
            <p className="text-white/70 text-sm mt-2">收入、支出、利润与资金流向一屏尽览</p>
          </div>
          <div className="flex flex-col items-end gap-2">
            {/* 客户切换器：仅影响下方「往来账龄与超期」区块 */}
            <label className="flex items-center gap-2 text-sm text-white/85">
              <span className="text-white/70">客户</span>
              <select
                value={partyId}
                onChange={(e) => pickParty(e.target.value)}
                className="rounded-lg bg-white/15 text-white ring-1 ring-white/25 px-3 py-1.5 text-sm max-w-[15rem] focus:outline-none focus:ring-white/50 [&>option]:text-slate-800"
                title="切换后往来账龄区块按该客户出数；选择仅自己可见"
              >
                <option value="">全部客户（我可见范围）</option>
                {parties.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </label>
            <span className={`badge ${data.ai.available ? 'bg-white/15 text-white ring-1 ring-white/25' : 'bg-black/20 text-white/70'}`}>
              🤖 ZUnicorn AI {data.ai.available ? '· 双引擎在线' : '· 规则引擎兜底'}
            </span>
            <Link to="/reports" className="text-sm text-white/85 hover:text-white underline-offset-4 hover:underline">查看完整报表 →</Link>
          </div>
        </div>
      </div>

      {/* 本月经营指标 */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        {monthCards.map((c, i) => (
          <Link
            key={c.label}
            to="/reports"
            className="card card-hover card-pad animate-fade-in-up group"
            style={{ animationDelay: `${i * 60}ms` }}
          >
            <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${c.grad} grid place-items-center text-lg shadow-glow-sm mb-3`}>
              {c.icon}
            </div>
            <div className="text-xs text-slate-500 mb-1">{c.label}</div>
            <div className={`kpi-value ${c.tone}`}>¥{formatAmount(c.value)}</div>
            {c.sub && <div className="text-[10px] text-slate-400 mt-1">{c.sub}</div>}
          </Link>
        ))}
      </div>

      {/* 往来账龄与超期预警（选中客户时后端已换为该客户口径） */}
      <div className="card card-pad">
        <div className="flex items-center justify-between mb-4">
          <h3 className="section-title">
            ⏳ 往来账龄与超期{data.party ? <span className="text-brand-600"> · 客户：{data.party.name}</span> : ''}
          </h3>
          <Link to="/reports?tab=aging" className="btn btn-soft btn-sm">账龄分析明细 →</Link>
        </div>
        {data.party && (
          <p className="text-[11px] text-slate-400 mb-3 -mt-2">当前仅统计选中客户的往来口径；收入、支出与待办仍为公司整体。</p>
        )}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <AgingTile label="应收未结" value={data.aging.receivableTotal} sub={data.party ? `${data.aging.receivableAvgDays} 天最长账龄` : `${data.aging.receivableAvgDays} 天加权平均`} tone="text-blue-600" bar="bg-blue-500" max={Math.max(data.aging.receivableTotal, data.aging.payableTotal, 1)} />
          <AgingTile label="应付未付" value={data.aging.payableTotal} sub={data.party ? `${data.aging.payableAvgDays} 天最长账龄` : `${data.aging.payableAvgDays} 天加权平均`} tone="text-violet-600" bar="bg-violet-500" max={Math.max(data.aging.receivableTotal, data.aging.payableTotal, 1)} />
          <Link to="/reports?tab=aging" className="rounded-xl border border-slate-100 bg-slate-50/60 p-4 hover:bg-white hover:shadow-card transition-all">
            <div className="text-sm font-medium text-slate-600">超期未结（信用期 30 天）</div>
            <div className="text-2xl font-bold font-mono text-red-600 mt-1">¥{formatAmount(data.aging.overdueAmount)}</div>
            <div className="text-[11px] text-slate-400 mt-2">{data.aging.overdueCount} 笔·最长 {data.aging.maxDays} 天</div>
          </Link>
          <Link to="/monitor" className="rounded-xl border border-slate-100 bg-slate-50/60 p-4 hover:bg-white hover:shadow-card transition-all">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-slate-600">待处理财务预警</span>
              {data.signals.alertUnread > 0 && <span className="badge bg-red-100 text-red-700">NEW</span>}
            </div>
            <div className={`text-2xl font-bold font-mono mt-1 ${data.signals.alertUnread > 0 ? 'text-red-600' : 'text-slate-400'}`}>
              {data.signals.alertUnread}
            </div>
            <div className="text-[11px] text-slate-400 mt-2">去信号中心扫描与跟进 →</div>
          </Link>
        </div>
      </div>

      {/* 我负责的客户（8.4 数据归属看板）：点行快捷切换 */}
      <MyClientsBoard onPick={(id) => { pickParty(id); window.scrollTo({ top: 0, behavior: 'smooth' }); }} />

      {/* 待办事项 */}
      <div>
        <h3 className="section-title mb-3">📌 待办事项</h3>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {todos.map((t) => (
            <Link key={t.label} to={t.to} className="card card-hover p-4 flex flex-col">
              <div className="flex items-center gap-2 mb-2">
                <span className={`w-2 h-2 rounded-full ${t.chip}`} />
                <span className="text-xs text-slate-500">{t.label}</span>
              </div>
              <div className="text-3xl font-bold font-mono text-slate-800">{t.value}</div>
            </Link>
          ))}
        </div>
      </div>

      {/* 收款分类待确认 */}
      <div className="card card-pad">
        <div className="flex items-center justify-between mb-4">
          <h3 className="section-title">💬 收款分类待确认</h3>
          <Link to="/wechat" className="btn btn-soft btn-sm">去处理 →</Link>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {pending.map((p) => (
            <Link key={p.label} to="/wechat" className="rounded-xl border border-slate-100 bg-slate-50/60 p-4 hover:bg-white hover:shadow-card transition-all group">
              <div className="flex items-baseline justify-between">
                <span className="text-sm font-medium text-slate-600">{p.label}</span>
                <span className={`text-2xl font-bold font-mono ${p.tone}`}>{p.value}</span>
              </div>
              <div className="mt-3 h-1 rounded-full bg-slate-200 overflow-hidden">
                <div className={`h-full ${p.bar}`} style={{ width: p.value ? '100%' : '0%' }} />
              </div>
              <div className="text-[11px] text-slate-400 mt-2">{p.hint}</div>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}

/** 往来未结额 tile（带横向对比条） */
function AgingTile({ label, value, sub, tone, bar, max }: {
  label: string; value: number; sub: string; tone: string; bar: string; max: number;
}) {
  return (
    <Link to="/reports?tab=aging" className="rounded-xl border border-slate-100 bg-slate-50/60 p-4 hover:bg-white hover:shadow-card transition-all">
      <div className="text-sm font-medium text-slate-600">{label}</div>
      <div className={`text-2xl font-bold font-mono mt-1 ${tone}`}>¥{formatAmount(value)}</div>
      <div className="mt-3 h-1 rounded-full bg-slate-200 overflow-hidden">
        <div className={`h-full ${bar}`} style={{ width: `${Math.min(100, Math.abs(value) / (max || 1) * 100).toFixed(1)}%` }} />
      </div>
      <div className="text-[11px] text-slate-400 mt-2">{sub}</div>
    </Link>
  );
}

/**
 * 8.4「我负责的客户」看板：基于按 owner 圈定的账龄 byCounterparty（命中档案 id）聚合，
 * 展示当前登录成员负责的客户的应收/应付未结与超期。未登录隐藏；无负责客户时给出引导。
 * 点击有归户命中（partyId）的行→通过 onPick 联动顶部客户切换器。
 */
function MyClientsBoard({ onPick }: { onPick: (partyId: string) => void }) {
  const me = getStoredUser();
  const myId = me?.id; // 稳定依赖：getStoredUser() 每次渲染返回新对象，直接进 deps 会触发重取风暴
  const [rows, setRows] = useState<Array<CounterpartyStat & { side: 'receivable' | 'payable' }>>([]);
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!myId) return;
    let alive = true;
    (async () => {
      try {
        const [parties, aging] = await Promise.all([
          partiesApi.list({ pageSize: 500 }),
          reportsApi.aging({ termsDays: 30 }),
        ]);
        const myIds = new Set(parties.parties.filter((p) => p.isActive && p.ownerUserId === myId).map((p) => p.id));
        const picked: Array<CounterpartyStat & { side: 'receivable' | 'payable' }> = [];
        for (const g of aging.groups) {
          for (const c of g.byCounterparty) {
            if (c.partyId && myIds.has(c.partyId)) picked.push({ ...c, side: g.side });
          }
        }
        if (!alive) return;
        picked.sort((a, b) => b.amount - a.amount);
        setRows(picked);
        setCount(myIds.size);
      } catch {
        /* 看板为增强项，失败时静默不阻断主仪表盘 */
      }
    })();
    return () => { alive = false; };
  }, [myId]);

  if (!me) return null;

  const rec = rows.filter((r) => r.side === 'receivable');
  const pay = rows.filter((r) => r.side === 'payable');
  const sum = (a: typeof rows) => a.reduce((s, r) => s + r.amount, 0);
  const overdueAmount = rows.reduce((s, r) => s + r.overdueAmount, 0);
  const overdueCount = rows.reduce((s, r) => s + r.overdueCount, 0);

  return (
    <div className="card card-pad">
      <div className="flex items-center justify-between mb-4">
        <h3 className="section-title">🧑‍💼 我负责的客户</h3>
        <Link to="/parties" className="btn btn-soft btn-sm">档案与负责人 →</Link>
      </div>
      {count === 0 ? (
        <p className="text-sm text-slate-500">
          你目前还没有负责的往来客户。{me.role === 'admin' || me.role === 'senior_accountant'
            ? '可在「往来单位」为档案指派负责人，或新建时默认挂到你名下。'
            : '如需查看某客户账龄，请联系管理员将其档案指派给你。'}
        </p>
      ) : (
        <>
          <div className="grid grid-cols-3 gap-3 mb-4">
            <div className="rounded-xl border border-slate-100 bg-slate-50/60 p-4">
              <div className="text-xs text-slate-500">应收未结</div>
              <div className="text-2xl font-bold font-mono text-blue-600 mt-1">¥{formatAmount(sum(rec))}</div>
            </div>
            <div className="rounded-xl border border-slate-100 bg-slate-50/60 p-4">
              <div className="text-xs text-slate-500">应付未结</div>
              <div className="text-2xl font-bold font-mono text-violet-600 mt-1">¥{formatAmount(sum(pay))}</div>
            </div>
            <div className="rounded-xl border border-slate-100 bg-slate-50/60 p-4">
              <div className="text-xs text-slate-500">超期未收/付</div>
              <div className={`text-2xl font-bold font-mono mt-1 ${overdueAmount > 0.004 ? 'text-red-600' : 'text-slate-400'}`}>¥{formatAmount(overdueAmount)}</div>
              <div className="text-[11px] text-slate-400 mt-1">{overdueCount} 笔 · 共 {count} 家</div>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-slate-500 text-left text-xs">
                <tr>
                  <th className="py-1.5 pr-4">客户</th>
                  <th className="py-1.5 pr-4">方向</th>
                  <th className="py-1.5 pr-4 text-right">未结</th>
                  <th className="py-1.5 pr-4 text-right">其中超期</th>
                  <th className="py-1.5 text-right">最长账龄</th>
                </tr>
              </thead>
              <tbody>
                {rows.slice(0, 8).map((r) => (
                  <tr
                    key={`${r.partyId}-${r.side}`}
                    className={`border-t border-slate-100 ${r.partyId ? 'cursor-pointer hover:bg-slate-50' : ''}`}
                    onClick={() => { if (r.partyId) onPick(r.partyId); }}
                    title={r.partyId ? '点击切换为该客户口径' : undefined}
                  >
                    <td className="py-2 pr-4 font-medium text-slate-800">{r.counterparty}</td>
                    <td className="py-2 pr-4">
                      <span className={`text-xs px-2 py-0.5 rounded-full ${r.side === 'receivable' ? 'bg-blue-50 text-blue-600' : 'bg-violet-50 text-violet-600'}`}>
                        {r.side === 'receivable' ? '应收' : '应付'}
                      </span>
                    </td>
                    <td className="py-2 pr-4 text-right font-mono">¥{formatAmount(r.amount)}</td>
                    <td className={`py-2 pr-4 text-right font-mono ${r.overdueAmount > 0.004 ? 'text-red-600' : 'text-slate-400'}`}>¥{formatAmount(r.overdueAmount)}</td>
                    <td className="py-2 text-right font-mono text-slate-600">{r.maxDays} 天</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {rows.length > 8 && <p className="text-[11px] text-slate-400 mt-2">仅展示前 8 项，完整明细见报表中心·账龄分析。</p>}
          </div>
        </>
      )}
    </div>
  );
}
